import {useEffect, useId, useMemo, useRef, useState, type ReactNode} from 'react';
import {TEAM_ROLES} from '../src/team';
import type {NativeAgentView, NativeChatDelegation, NativeChatFileChange, NativeChatTurnActivity} from '../src/native-hermes-types';
import {userMessageDisplayText} from './lib/conversation-messages';
import {ErrorNotice} from './components';
import type {NativeTeamProjection, TeamMemberView} from './team-workspace';
import './chat-activity.css';

type AgentDetails = {
  agents?: readonly NativeAgentView[];
  onSelectAgent?: (sessionId: string | null) => void;
  messagePrefix?: string;
};
type DetailsTarget = {kind: 'files'; path: string | null} | {kind: 'agents'} | null;

type ChatCurrentWorkView = {status: string; rows: Array<{sessionId: string; name: string | null; description: string}>};

/** Read the current native snapshot without storing another work state. */
export function chatCurrentWork(projection: NativeTeamProjection, selectedSessionId: string): ChatCurrentWorkView | null {
  if (projection.status !== 'ready') return null;
  const members = [...projection.members, ...projection.descendants];
  const selected = members.find(member => member.sessionId === selectedSessionId);
  if (!selected) return null;
  const active = (member: TeamMemberView) => member.executionState === 'working'
    || member.executionState === 'resuming' || member.executionState === 'waiting_input'
    || member.executionState === 'waiting_agents';
  const rows = members.filter(member => active(member) && (member === selected
    || selectedSessionId === projection.sessionId));
  if (!rows.length) return null;
  return {status: [...new Set(rows.map(member => member.status))].join(' · '), rows: rows.map(member => {
      const task = projection.tasks.find(task => task.group === 'in_progress' && (typeof task.assignee === 'object'
        ? task.assignee?.sessionId === member.sessionId
        : task.assignee === member.roleId && projection.members.some(primary => primary.sessionId === member.sessionId)));
      const description = member.executionState === 'working' ? task?.title || member.activity || member.status : member.status;
      const name = projection.descendants.find(child => child.sessionId === member.sessionId)?.name
        ?? TEAM_ROLES.find(role => role.id === member.roleId)!.name;
      return {sessionId: member.sessionId, name: member === selected ? null : name, description};
    })};
}

export function ChatCurrentWork({work, detailsOnly = false}: {work: ChatCurrentWorkView; detailsOnly?: boolean}) {
  const content = <div className="chat-current-work-rows">
    {work.rows.map(row => <p key={row.sessionId} data-agent-session={row.sessionId}>
      {row.name !== null && <span>{row.name}: </span>}{row.description}
    </p>)}
  </div>;
  if (detailsOnly) return content;
  return <details className="chat-current-work chat-activity-disclosure">
    <summary><span>Current work · {work.status}</span><span className="chat-activity-toggle-closed">Show details</span><span className="chat-activity-toggle-open">Hide details</span></summary>
    {content}
  </details>;
}

function ActivityIcon({kind}: {kind: 'command' | 'agents' | 'chevron' | 'close' | 'file'}) {
  return <svg className={`chat-activity-icon chat-activity-icon-${kind}`} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {kind === 'command' && <><rect x="3" y="4" width="18" height="16" rx="3"/><path d="m7 9 3 3-3 3m6 0h4"/></>}
    {kind === 'agents' && <><rect x="5" y="7" width="14" height="13" rx="4"/><path d="M12 3v4M2 12v4m20-4v4m-13-4v2m6-2v2m-6 3h6"/><circle cx="12" cy="3" r="1"/></>}
    {kind === 'chevron' && <path d="m9 5 7 7-7 7"/>}
    {kind === 'close' && <path d="m6 6 12 12M18 6 6 18"/>}
    {kind === 'file' && <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6M8 13h8m-8 4h5"/></>}
  </svg>;
}

function duration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds % 3600 / 60);
  const remainder = seconds % 60;
  return [hours ? `${hours}h` : '', minutes || hours ? `${minutes}m` : '', `${remainder}s`].filter(Boolean).join(' ');
}

/** Count from native turn timestamps, using the browser clock only between live observations. */
export function ChatWorkTimer({turn, observedAt, connected, inline = false, statusLabel}: {
  turn: NativeChatTurnActivity; observedAt: string; connected: boolean; inline?: boolean; statusLabel?: string;
}) {
  const anchor = useMemo(() => performance.now(), [turn.id, turn.status, turn.finishedAt, observedAt, connected]);
  const [clock, setClock] = useState(() => performance.now());
  const ticking = connected && turn.status === 'running' && turn.finishedAt === null;
  useEffect(() => {
    if (!ticking) return;
    const interval = window.setInterval(() => setClock(performance.now()), 1000);
    return () => window.clearInterval(interval);
  }, [ticking, anchor]);
  const start = Date.parse(turn.startedAt);
  const end = turn.finishedAt === null ? Date.parse(observedAt) : Date.parse(turn.finishedAt);
  const valid = Number.isFinite(start) && Number.isFinite(end) && end >= start
    && (turn.status === 'running' || turn.finishedAt !== null);
  const seconds = valid ? Math.floor((end - start + (ticking ? Math.max(0, clock - anchor) : 0)) / 1000) : null;
  let label: string | null = null;
  if (turn.status === 'running') label = connected
    ? seconds === null ? statusLabel ?? 'Working' : statusLabel && statusLabel !== 'Working'
      ? `${statusLabel} · ${duration(seconds)}` : `Working for ${duration(seconds)}`
    : seconds === null ? 'Work connection interrupted' : `Last seen working · ${duration(seconds)}`;
  else if (seconds !== null) label = `${turn.status === 'completed' ? 'Worked for' : turn.status === 'interrupted'
    ? 'Stopped after' : turn.status === 'failed' ? 'Failed after' : 'Recorded work ·'} ${duration(seconds)}`;
  if (label === null) return null;
  return inline ? <span className="chat-work-timer-inline" data-state={turn.status}>{label}</span>
    : <div className="chat-work-timer" data-state={turn.status} title="Elapsed time recorded for this turn">{label}</div>;
}

function FileCounts({additions, deletions, partial = false}: {additions: number | null; deletions: number | null; partial?: boolean}) {
  if (additions === null && deletions === null) return null;
  return <span className="chat-file-counts" title={partial ? 'Partial recorded edit counts' : 'Recorded edit counts'}>
    {additions !== null && <span className="chat-lines-added" aria-label={`${partial ? 'At least ' : ''}${additions} lines added`}>+{additions}</span>}
    {deletions !== null && <span className="chat-lines-removed" aria-label={`${partial ? 'At least ' : ''}${deletions} lines removed`}>−{deletions}</span>}
    {partial && <span>recorded</span>}
  </span>;
}

function fileTotals(files: readonly NativeChatFileChange[]): {additions: number | null; deletions: number | null} {
  return {
    additions: files.every(file => file.additions !== null) ? files.reduce((total, file) => total + file.additions!, 0) : null,
    deletions: files.every(file => file.deletions !== null) ? files.reduce((total, file) => total + file.deletions!, 0) : null,
  };
}

function delegatesForTurn(turn: NativeChatTurnActivity): NativeChatDelegation[] {
  return [...new Map(turn.agents.map(agent => [agent.sessionId ?? agent.id, agent])).values()];
}

function agentName(delegation: NativeChatDelegation, agent: NativeAgentView | undefined, messagePrefix?: string): string {
  const roleId = agent?.roleId ?? delegation.roleId;
  // A native role may precede the UI's catalog after an image upgrade.
  const role = roleId ? TEAM_ROLES.find(candidate => candidate.id === roleId) : undefined;
  return role?.name ?? (messagePrefix && agent?.title ? userMessageDisplayText(agent.title, messagePrefix, {folded: true}) : 'Assigned agent');
}

function ActivityDialog({title, children, onClose}: {title: string; children: ReactNode; onClose: () => void}) {
  const ref = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const titleId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
      heading.current?.focus({preventScroll: true});
    }
  }, []);
  return <dialog className="chat-activity-dialog" ref={ref} aria-labelledby={titleId} onClose={onClose}
    onClick={event => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
        event.currentTarget.close();
      }
    }}>
    <header className="chat-activity-dialog-header"><h2 ref={heading} id={titleId} tabIndex={-1}>{title}</h2>
      <button type="button" aria-label={`Close ${title.toLowerCase()}`} onClick={() => ref.current?.close()}><ActivityIcon kind="close"/></button>
    </header>
    <div className="chat-activity-dialog-body">{children}</div>
  </dialog>;
}

function FileDiff({file, partial}: {file: NativeChatFileChange; partial: boolean}) {
  return <section className="chat-file-diff" aria-label={`Changes to ${file.path}`}>
    <div className="chat-file-diff-heading"><h3>{file.path}</h3><FileCounts additions={file.additions} deletions={file.deletions} partial={partial}/></div>
    {file.diff === null ? <p className="chat-activity-unavailable">No saved diff for this file.</p>
      : <pre className="chat-diff-lines" tabIndex={0} aria-label="Recorded file diff"><code>{file.diff.split('\n').map((line, index) =>
        <span key={index} className={line.startsWith('+++') || line.startsWith('---') ? 'chat-diff-heading'
          : line.startsWith('+') ? 'chat-diff-added' : line.startsWith('-') ? 'chat-diff-removed'
            : line.startsWith('@@') ? 'chat-diff-location' : undefined}>{line || '\u00a0'}</span>)}</code></pre>}
    {file.diffTruncated && file.diff !== null && <p className="chat-activity-unavailable">Partial diff.</p>}
  </section>;
}

function DetailsDialog({turn, target, agents = [], onSelectAgent, messagePrefix, onClose}: AgentDetails & {
  turn: NativeChatTurnActivity; target: Exclude<DetailsTarget, null>; onClose: () => void;
}) {
  if (target.kind === 'files') {
    const selected = target.path === null ? null : turn.files.find(file => file.path === target.path) ?? null;
    return <ActivityDialog title={selected ? 'File changes' : 'Changed files'} onClose={onClose}>
      {(selected ? [selected] : turn.files).map(file => <FileDiff key={file.path} file={file} partial={turn.detailsTruncated}/>)}
    </ActivityDialog>;
  }
  return <ActivityDialog title="Agents" onClose={onClose}>
    <DelegatedAgents turn={turn} agents={agents} {...(onSelectAgent ? {onSelectAgent: id => {onSelectAgent(id); onClose();}} : {})}
      {...(messagePrefix === undefined ? {} : {messagePrefix})}/>
  </ActivityDialog>;
}

function DelegatedAgents({turn, agents = [], onSelectAgent, messagePrefix}: AgentDetails & {turn: NativeChatTurnActivity}) {
  return <ul className="chat-activity-agents">{delegatesForTurn(turn).map(delegation => {
      const agent = agents.find(candidate => candidate.sessionId === delegation.sessionId);
      return <li key={delegation.id}>
        <div className="chat-activity-agent-heading"><ActivityIcon kind="agents"/>
          <strong>{agentName(delegation, agent, messagePrefix)}</strong><span>{delegation.status}</span>
        </div>
        {agent?.assignedTask && messagePrefix && <p><span className="chat-activity-task-label">Current assignment</span>{userMessageDisplayText(agent.assignedTask, messagePrefix)}</p>}
        {agent?.error && <ErrorNotice title="There was a problem with this agent" error={{name: 'AgentError', message: agent.error}}/>}
        {agent && onSelectAgent && <button type="button" className="chat-activity-text-button" onClick={() => onSelectAgent(agent.sessionId)}>Open conversation</button>}
      </li>;
    })}</ul>;
}

function ChangedFiles({turn, onOpen, expanded = false}: {turn: NativeChatTurnActivity; onOpen: (path: string | null) => void; expanded?: boolean}) {
  const shown = expanded ? turn.files : turn.files.slice(0, 3);
  const rest = turn.files.length - shown.length;
  const heading = <><span>{turn.detailsTruncated ? 'At least ' : ''}{turn.files.length} {turn.files.length === 1 ? 'file' : 'files'} with recorded changes</span>
    <FileCounts {...fileTotals(turn.files)} partial={turn.detailsTruncated}/></>;
  const files = <ul>{shown.map(file => <li key={file.path}><button type="button" onClick={() => onOpen(file.path)} aria-label={`View changes to ${file.path}`}>
      <span className="chat-file-path" title={file.path}>{file.path}</span>
      <FileCounts additions={file.additions} deletions={file.deletions} partial={turn.detailsTruncated}/>
    </button></li>)}</ul>;
  if (expanded) return <section className="chat-recorded-files"><p className="chat-recorded-heading">{heading}</p>{files}</section>;
  return <details className="chat-changed-files" open>
    <summary>{heading}<ActivityIcon kind="chevron"/></summary>
    {files}
    {rest > 0 && <button className="chat-files-more" type="button" onClick={() => onOpen(null)}>
      <span>View {rest} more {turn.detailsTruncated ? 'recorded ' : ''}{rest === 1 ? 'file' : 'files'}</span><ActivityIcon kind="chevron"/>
    </button>}
  </details>;
}

/** Display only the execution evidence already projected by the native runtime. */
export function ChatTurnActivityDetails({turn, connected, agents, onSelectAgent, messagePrefix, currentWork, expanded = false}: AgentDetails & {
  turn: NativeChatTurnActivity; connected: boolean; currentWork?: ChatCurrentWorkView; expanded?: boolean;
}) {
  const [target, setTarget] = useState<DetailsTarget>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const commands = turn.tools.filter(tool => tool.kind === 'command').length;
  const tools = turn.tools.length - commands;
  const callsLabel = (turn.detailsTruncated ? 'Recorded ' : '')
    + [commands ? `${commands} ${commands === 1 ? 'command' : 'commands'}` : '',
      tools ? `${tools} ${tools === 1 ? 'tool' : 'tools'}` : ''].filter(Boolean).join(' and ');
  const delegations = delegatesForTurn(turn);
  const toolSteps = <ol className="chat-tool-steps">{turn.tools.map(tool => <li key={tool.id}>
    <div className="chat-tool-activity-heading"><span>{tool.label}</span><small>{tool.status === 'running' && !connected ? 'Last seen running' : tool.status}</small></div>
    {tool.detail && <p>{tool.detail}</p>}
  </li>)}</ol>;
  return <div className={expanded ? 'chat-recorded-activity' : 'chat-activity-expanded'}>
    {currentWork && <ChatCurrentWork work={currentWork} detailsOnly/>}
    {turn.files.length > 0 && <ChangedFiles turn={turn} expanded={expanded} onOpen={path => setTarget({kind: 'files', path})}/>}
    {turn.tools.length > 0 && (expanded ? <section className="chat-recorded-tools"><p className="chat-recorded-heading">{callsLabel}</p>{toolSteps}</section>
      : <details className="chat-tool-activity" onToggle={event => setToolsOpen(event.currentTarget.open)}>
        <summary><ActivityIcon kind="command"/><span>{callsLabel}</span><ActivityIcon kind="chevron"/></summary>
        {toolsOpen && toolSteps}
      </details>)}
    {delegations.length > 0 && (expanded ? <section className="chat-recorded-delegations">
      <p className="chat-recorded-heading">Assigned {turn.detailsTruncated ? 'at least ' : ''}{delegations.length} {delegations.length === 1 ? 'agent' : 'agents'}</p>
      <DelegatedAgents turn={turn} {...(agents === undefined ? {} : {agents})}
        {...(onSelectAgent === undefined ? {} : {onSelectAgent})} {...(messagePrefix === undefined ? {} : {messagePrefix})}/>
    </section> : <button type="button" className="chat-activity-summary" onClick={() => setTarget({kind: 'agents'})}>
      <ActivityIcon kind="agents"/><span>Assigned {turn.detailsTruncated ? 'at least ' : ''}{delegations.length} {delegations.length === 1 ? 'agent' : 'agents'}</span><ActivityIcon kind="chevron"/>
    </button>)}
    {target && <DetailsDialog turn={turn} target={target} {...(agents === undefined ? {} : {agents})}
      {...(onSelectAgent === undefined ? {} : {onSelectAgent})} {...(messagePrefix === undefined ? {} : {messagePrefix})} onClose={() => setTarget(null)}/>}
    {turn.detailsTruncated && <p className="chat-activity-unavailable">This record does not include all work details.</p>}
  </div>;
}

export function ChatTurnActivity({turn, observedAt, connected, agents, onSelectAgent, messagePrefix, currentWork}: AgentDetails & {
  turn: NativeChatTurnActivity; observedAt: string; connected: boolean; currentWork?: ChatCurrentWorkView;
}) {
  const runningTool = turn.status === 'running' ? turn.tools.filter(tool => tool.status === 'running').at(-1) : undefined;
  const briefTool = runningTool ? runningTool.label.length > 88 ? `${runningTool.label.slice(0, 85)}…` : runningTool.label : null;
  const summary = <span className="chat-activity-summary-status"><ChatWorkTimer turn={turn} observedAt={observedAt} connected={connected} inline {...(currentWork ? {statusLabel: currentWork.status} : {})}/>
    {briefTool && <span className="chat-activity-brief-tool">{briefTool}</span>}</span>;
  if (!turn.tools.length && !turn.files.length && !turn.agents.length && !currentWork) {
    if (turn.status !== 'running' && turn.finishedAt === null) return null;
    return <div className="chat-turn-activity chat-activity-static-summary">{summary}</div>;
  }
  return <details className="chat-turn-activity chat-activity-disclosure" aria-label="Work for this message">
    <summary>{summary}
      <span className="chat-activity-toggle-closed">Show details</span><span className="chat-activity-toggle-open">Hide details</span>
    </summary>
    <ChatTurnActivityDetails key={turn.id} turn={turn} connected={connected} {...(agents === undefined ? {} : {agents})}
      {...(onSelectAgent === undefined ? {} : {onSelectAgent})} {...(messagePrefix === undefined ? {} : {messagePrefix})}
      {...(currentWork === undefined ? {} : {currentWork})}/>
  </details>;
}

export function ChatActivityChips({turn, agents, onSelectAgent, messagePrefix}: AgentDetails & {turn: NativeChatTurnActivity | null}) {
  const [target, setTarget] = useState<DetailsTarget>(null);
  useEffect(() => {setTarget(null);}, [turn?.id]);
  if (!turn) return null;
  const delegations = delegatesForTurn(turn);
  if (!turn.files.length && !delegations.length) return null;
  return <div className="chat-activity-chips" aria-label="Current work details">
    {turn.files.length > 0 && <button type="button" onClick={() => setTarget({kind: 'files', path: null})}>
      <span>{turn.files.length} {turn.detailsTruncated ? 'recorded ' : ''}{turn.files.length === 1 ? 'file' : 'files'}</span><FileCounts {...fileTotals(turn.files)} partial={turn.detailsTruncated}/>
    </button>}
    {delegations.length > 0 && <button type="button" onClick={() => setTarget({kind: 'agents'})}>
      <ActivityIcon kind="agents"/><span>{delegations.length} {turn.detailsTruncated ? 'recorded ' : ''}{delegations.length === 1 ? 'agent' : 'agents'}</span>
    </button>}
    {target && <DetailsDialog turn={turn} target={target} {...(agents === undefined ? {} : {agents})}
      {...(onSelectAgent === undefined ? {} : {onSelectAgent})} {...(messagePrefix === undefined ? {} : {messagePrefix})} onClose={() => setTarget(null)}/>}
  </div>;
}
